import i18n from "i18next";

import { EventAnswersLabels } from "@/constants/answersLabels";
import { UnitTypes } from "@/constants/bodyMetrics";
import { ExperimentIds } from "@/constants/experiments";
import { FitnessGoals } from "@/constants/fitnessGoal";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { useLocalization } from "@/hooks/useLocalization";
import { formatDate } from "@/utils/dateFormatter";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { useFlowManager } from "@/utils/providers/FlowProvider";
import { calculateWeight } from "@/utils/services/weightFormatters";
import useAnswers from "@/utils/useAnswers";

export const useWeightPredictor = () => {
    const { getAnswersValue, answers } = useAnswers();
    const { isFemale } = useGender();
    const { isEnglish } = useLocalization();
    const {
        isFlowType: { friends },
    } = useFlowManager({});

    const { isTest: isSeniorsFunnelExp } = useExperiment({
        experimentId: ExperimentIds.SENIORS_FUNNEL,
    });

    const primaryGoal = getAnswersValue(QUESTIONS_IDS.PRIMARY_GOAL);
    const isWeightPredictor = Boolean(
        isFemale &&
            primaryGoal === FitnessGoals.LoseWeight &&
            isEnglish &&
            !friends &&
            !isSeniorsFunnelExp
    );

    const currentWeight = getAnswersValue(QUESTIONS_IDS.CURRENT_WEIGHT);
    const targetWeight = getAnswersValue(QUESTIONS_IDS.TARGET_WEIGHT);
    const showIntermediateResult = getAnswersValue(QUESTIONS_IDS.SHOW_INTERMEDIATE_RESULT);
    const intermediateWeightLoss = getAnswersValue(QUESTIONS_IDS.INTERMEDIATE_WEIGHT_LOSS);

    const weightLoss = showIntermediateResult
        ? intermediateWeightLoss
        : currentWeight - targetWeight;

    const predictedDrop = calculateWeight(weightLoss, answers.units as UnitTypes, 1);

    const event = getAnswersValue(QUESTIONS_IDS.EVENT_MOTIVATION);
    const eventLabel = EventAnswersLabels[event];
    const eventDate = getAnswersValue(QUESTIONS_IDS.EVENT_DATE) as Date | "skip";
    const isEventDate = !!eventDate && eventDate !== "skip";
    const weightPredictDate = getAnswersValue(QUESTIONS_IDS.WEIGHT_PREDICT_DATE);
    const date = showIntermediateResult ? eventDate : (weightPredictDate as Date);

    const predictedDropTitle =
        isEventDate || !showIntermediateResult
            ? i18n.t("your_progress_by_date", {
                  date:
                      isWeightPredictor && isEventDate
                          ? eventLabel.toLowerCase()
                          : formatDate(date),
              })
            : i18n.t("your_4w_progress");

    return { isWeightPredictor, predictedDrop, predictedDropTitle };
};
